import { Tr } from '@chakra-ui/react';
import { useGenericContext } from 'components/contexts/useGenericContext';
import { SpotlightForm } from 'components/forms';
import { Td, TdActions, TdTag } from 'components/lib';
import { CustomTable, FormDrawer } from 'components/modules';
import { FormikProps } from 'formik';
import { getId, getResults, isResultsEmpty } from 'helpers';
import fp from 'lodash/fp';
import { nanoid } from 'nanoid';
import { useCallback, useRef } from 'react';
import {
  ISpotlightPayload,
  ISpotlightResource,
} from 'services/resources/spotlight/types.d';

import {
  getCreated,
  getDescription,
  getExpirationDate,
  getSpotlightType,
  getTitle,
  HEADERS,
} from '../helpers';

export const Page: React.FC = (): JSX.Element => {
  const {
    active,
    defaultPatch,
    defaultRestore,
    isDrawerOpen,
    onPrepareDelete,
    onPrepareDrawer,
    registry,
    state,
    toggleDrawer,
  } = useGenericContext<ISpotlightResource>();

  const ref = useRef<FormikProps<ISpotlightPayload>>(null);

  const prepareFormValues = useCallback(
    () => ({
      ...registry,
      spotlight_type: fp.get(['spotlight_type', 'id'])(registry),
    }),
    [registry],
  );

  const prepareOnSubmit = useCallback(() => {
    if (ref?.current) ref?.current?.submitForm();
  }, [ref]);

  return (
    <>
      <CustomTable
        isEmpty={isResultsEmpty(state)}
        headers={HEADERS}
        isLoading={state?.loading}
        mx={8}
      >
        {getResults(state)?.map((row: ISpotlightResource) => (
          <Tr key={nanoid()} position="relative">
            <Td>{getId(row)}</Td>
            <Td>{getTitle(row)}</Td>
            <Td>{getDescription(row)}</Td>
            <Td>{getExpirationDate(row)}</Td>
            <TdTag>{getSpotlightType(row)}</TdTag>
            <Td>{getCreated(row)}</Td>
            <TdActions
              isActive={active}
              onDelete={() => onPrepareDelete(row)}
              onRestore={() => defaultRestore(row, { is_active: true })}
              onUpdate={() => onPrepareDrawer(row)}
            />
          </Tr>
        ))}
      </CustomTable>

      <FormDrawer
        isOpen={isDrawerOpen}
        isDisabled={!!ref?.current?.isSubmitting || !!ref?.current?.isValid}
        isLoading={!!ref.current?.isSubmitting}
        onClose={toggleDrawer}
        onSubmit={prepareOnSubmit}
      >
        <SpotlightForm
          initialValues={prepareFormValues()}
          onSubmit={defaultPatch}
          ref={ref}
        />
      </FormDrawer>
    </>
  );
};
